import { TurnstileApi, loadTurnstile } from './TurnstileWidget';

const CHALLENGE_TIMEOUT_MS = 20_000;

/** The Turnstile script could not be loaded (blocked, offline): callers may fall back. */
export class TurnstileLoadError extends Error {}
/** The caller aborted the request (e.g. the dialog was closed). */
export class TurnstileAbortError extends Error {}
/** Cloudflare answered with an error, or no token arrived in time. */
export class TurnstileChallengeError extends Error {}

type Options = {
  siteKey: string;
  /** Turnstile action; the server checks it ("free_month"). */
  action: string;
  locale: string;
  /** Element the widget is rendered into; Cloudflare shows UI there only if it needs an interaction. */
  container: HTMLElement;
  /** Abort (dialog closed): the widget is removed and the promise rejects with TurnstileAbortError. */
  signal?: AbortSignal;
};

/**
 * Mints one single-use Turnstile token without a visible widget
 * (execution 'execute', appearance 'interaction-only'). The widget is always
 * removed afterwards; call again for every submit.
 */
export const requestTurnstileToken = async ({ siteKey, action, locale, container, signal }: Options): Promise<string> => {
  if (signal?.aborted) throw new TurnstileAbortError('aborted');
  let turnstile: TurnstileApi;
  try {
    turnstile = await loadTurnstile();
  } catch (error) {
    throw new TurnstileLoadError(error instanceof Error ? error.message : 'Turnstile failed to load');
  }
  if (signal?.aborted) throw new TurnstileAbortError('aborted');
  return new Promise<string>((resolve, reject) => {
    let widgetId: string | null = null;
    let settled = false;
    const finish = (error: Error | null, token?: string) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      if (widgetId !== null) {
        try {
          turnstile.remove(widgetId);
        } catch {
          /* widget already gone */
        }
      }
      if (error) reject(error);
      else resolve(token as string);
    };
    const onAbort = () => finish(new TurnstileAbortError('aborted'));
    signal?.addEventListener('abort', onAbort);
    const timer = window.setTimeout(() => finish(new TurnstileChallengeError('Turnstile timeout')), CHALLENGE_TIMEOUT_MS);
    try {
      widgetId = turnstile.render(container, {
        sitekey: siteKey,
        action,
        language: ['nl', 'en', 'de'].includes(locale) ? locale : 'auto',
        execution: 'execute',
        appearance: 'interaction-only',
        callback: (token) => (token ? finish(null, token) : finish(new TurnstileChallengeError('Empty token'))),
        'error-callback': () => finish(new TurnstileChallengeError('Turnstile error')),
        'expired-callback': () => finish(new TurnstileChallengeError('Turnstile expired')),
      });
      if (!settled) turnstile.execute(widgetId);
    } catch (error) {
      finish(new TurnstileChallengeError(error instanceof Error ? error.message : 'Turnstile error'));
    }
  });
};
